import {
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Popover as AriaPopover,
  Key,
  ListBoxItemRenderProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { dialog } from '../../patterns/dialog';
import { ComponentProps, ReactElement } from 'react';
import { DropdownOption } from '../dropdownOptions/dropdownOption/dropdownOption';

export type SelectDropdownItem<T> = {
  label: string;
  value: Key;
  meta: T;
};

type Props<T> = {
  items: Iterable<SelectDropdownItem<T>>;
  enforceMinWidth?: boolean;
  enforceMaxHeight?: boolean;
  className?: string;
  renderOption?: (
    item: SelectDropdownItem<T>,
    itemInfo: ListBoxItemRenderProps,
  ) => ReactElement | null;
} & Omit<ComponentProps<typeof AriaPopover>, 'className'>;

export function SelectDropdown<T>(props: Props<T>) {
  const { items, renderOption, enforceMinWidth, enforceMaxHeight, className } =
    props;

  return (
    <AriaPopover style={{ minWidth: 'var(--trigger-width)' }}>
      <AriaListBox
        items={items}
        className={twMerge(
          dialog(),
          'p-8 outline-none',
          enforceMinWidth && 'min-w-[266px]',
          enforceMaxHeight && 'max-h-[168px] overflow-y-scroll',
          className,
        )}
      >
        {(item) => (
          <AriaListBoxItem
            className={twMerge(
              'focus-visible:bg-primary50 outline-none cursor-pointer rounded-4 transition-colors ease-out duration-200',
            )}
            textValue={item.label}
            id={item.value}
          >
            {(itemInfo) =>
              renderOption ? (
                renderOption(item, itemInfo)
              ) : (
                <DropdownOption label={item.label} {...itemInfo} />
              )
            }
          </AriaListBoxItem>
        )}
      </AriaListBox>
    </AriaPopover>
  );
}
